import styled from 'styled-components';
import { motion, useReducedMotion } from 'framer-motion';
import { colors, fonts } from '../theme';
import { HudPanel, Eyebrow, StatusDot, SectionBackdrop, StarFlareIcon } from '../components/HudFrame';
import galaxyImg from '../assets/space3.jpg';
import shipImg from '../assets/ship.jpg';
import { fadeUp, staggerContainer, revealViewport } from '../motion';

const Wrap = styled.section`
  position: relative;
`;

const Backdrop = styled(SectionBackdrop)`
  opacity: 0.85;
`;

const Main = styled.div`
  position: relative;
  z-index: 1;
  max-width: 960px;
  margin: 0 auto;
  padding: 4.5rem 2rem 5rem;
  color: ${colors.textPrimary};

  @media (max-width: 768px) {
    padding: 3rem 1.25rem 3.5rem;
  }
`;

const Title = styled.h2`
  font-family: ${fonts.display};
  font-size: 2.6rem;
  margin-bottom: 0.5rem;
  color: ${colors.textPrimary};
  letter-spacing: 0.06em;
  text-shadow: 0 4px 32px rgba(0,0,0,0.65);

  @media (max-width: 768px) {
    font-size: 1.8rem;
    letter-spacing: 0.03em;
  }
`;

const SubLabel = styled.p`
  font-family: ${fonts.mono};
  font-size: 0.75rem;
  letter-spacing: 0.12em;
  color: ${colors.textFaint};
  margin-bottom: 2.5rem;

  @media (max-width: 768px) {
    margin-bottom: 2rem;
  }
`;

const SectionTitle = styled.h3`
  font-family: ${fonts.mono};
  font-size: 0.78rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${colors.violet};
  margin: 3rem 0 1.25rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${colors.hairline};
  }

  &:first-of-type {
    margin-top: 0;
  }
`;

const StatGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.25rem;

  @media (max-width: 640px) {
    grid-template-columns: repeat(1, 1fr);
  }
`;

const StatCard = styled(motion(HudPanel))`
  padding: 1.75rem 1.5rem;
  text-align: center;
  background: rgba(6, 9, 17, 0.68);
`;

const StatValue = styled.div`
  font-family: ${fonts.display};
  font-size: 2.4rem;
  color: ${colors.amber};
  letter-spacing: 0.02em;
  line-height: 1.1;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const StatLabel = styled.div`
  font-family: ${fonts.mono};
  font-size: 0.68rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${colors.textFaint};
  margin-top: 0.6rem;
`;

const Panel = styled(motion(HudPanel))`
  padding: 1.75rem;
  background: rgba(6, 9, 17, 0.68);

  @media (max-width: 768px) {
    padding: 1.35rem;
  }
`;

const Text = styled.p`
  font-family: ${fonts.mono};
  font-size: 0.92rem;
  line-height: 1.75;
  color: ${colors.textDim};
  margin-bottom: 1rem;

  &:last-child {
    margin-bottom: 0;
  }

  strong {
    color: ${colors.amber};
    font-weight: 600;
  }
`;

const ConstellationHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.85rem;
  margin-bottom: 1rem;
`;

const ShipMark = styled.img`
  width: 34px;
  height: 34px;
  object-fit: contain;
  border-radius: 50%;
  border: 1px solid ${colors.violetDim};
  background: #000;
  filter: drop-shadow(0 0 8px rgba(167, 139, 250, 0.4));
  flex-shrink: 0;
`;

const ConstellationGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.85rem;

  @media (max-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }

  /* Center a lone chip in the final incomplete row */
  > :last-child:nth-child(3n + 1) {
    grid-column: 2;
  }

  @media (max-width: 640px) {
    > :last-child:nth-child(3n + 1) {
      grid-column: auto;
    }

    > :last-child:nth-child(odd) {
      grid-column: 1 / -1;
      justify-self: center;
      width: calc((100% - 0.85rem) / 2);
    }
  }
`;

const CompanyChip = styled(motion.div)`
  position: relative;
  border: 1px solid ${colors.hairline};
  background: rgba(143, 216, 255, 0.04);
  padding: 0.9rem 0.75rem;
  text-align: center;
  font-family: ${fonts.mono};
  font-size: 0.8rem;
  letter-spacing: 0.05em;
  color: ${colors.textPrimary};
  transition: border-color 0.25s ease, background 0.25s ease, transform 0.25s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;

  svg {
    width: 0.7em;
    height: 0.7em;
    color: ${colors.amber};
    opacity: 0;
    transition: opacity 0.25s ease;
  }

  &:hover {
    border-color: ${colors.amberDim};
    background: rgba(242, 169, 59, 0.06);
    transform: translateY(-2px);

    svg {
      opacity: 1;
    }
  }
`;

const companies = [
  "HF0", "Pylon", "Scale", "Hightouch", "Feathery",
  "TrueWind", "Italic", "Pallet", "Workflow",
  "Hydra DB", "Hyperspell", "Superpower", "New Lantern", "Apple Shortcuts", "Jam.dev",
  "Colare",
];

export function Community() {
  const prefersReducedMotion = useReducedMotion();
  const reveal = prefersReducedMotion
    ? { initial: 'show', whileInView: 'show', viewport: revealViewport }
    : { initial: 'hidden', whileInView: 'show', viewport: revealViewport };

  return (
    <Wrap>
      <Backdrop
        $image={galaxyImg}
        role="img"
        aria-label="A violet and amber spiral galaxy with a warm glowing core — the constellation of Mission Control companies sits inside this galaxy view"
      />
      <Main>
        <Eyebrow>
          <StatusDot />
          FILE 03 — NETWORK TELEMETRY
        </Eyebrow>
        <Title>Community</Title>
        <SubLabel>ALUMNI &amp; FRIENDS NETWORK · LIVE READOUT</SubLabel>

        <SectionTitle>Network Stats</SectionTitle>
        <StatGrid {...reveal} variants={staggerContainer()}>
          <StatCard variants={fadeUp}>
            <StatValue>$2B+</StatValue>
            <StatLabel>Venture Funding Raised</StatLabel>
          </StatCard>
          <StatCard variants={fadeUp}>
            <StatValue>$200B+</StatValue>
            <StatLabel>Combined Market Cap</StatLabel>
          </StatCard>
          <StatCard variants={fadeUp}>
            <StatValue>200+</StatValue>
            <StatLabel>Alumni &amp; Friends</StatLabel>
          </StatCard>
        </StatGrid>

        <SectionTitle>Constellation of Companies</SectionTitle>
        <Panel {...reveal} variants={fadeUp}>
          <ConstellationHeader>
            <ShipMark src={shipImg} alt="" aria-hidden="true" />
            <Text style={{ marginBottom: 0 }}>
              A sample of the companies alumni have gone on to build or join — hover any node
              to bring it into focus.
            </Text>
          </ConstellationHeader>
          <ConstellationGrid {...reveal} variants={staggerContainer(0.04)}>
            {companies.map((name) => (
              <CompanyChip key={name} variants={fadeUp}>
                <StarFlareIcon />
                {name}
              </CompanyChip>
            ))}
          </ConstellationGrid>
        </Panel>
      </Main>
    </Wrap>
  );
}
